import React, { Component, ErrorInfo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCollectedLogs, getBrowserInfo } from "@/utils/logCollector";

const CRASH_REPORT_COOLDOWN_KEY = "curio_last_crash_report";
const COOLDOWN_MS = 30_000;

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  /** Human-readable label used in the fallback UI and crash report (e.g. "Chat", "Mind map"). */
  section: string;
  /** Render a completely custom fallback. When provided, the built-in card is skipped. */
  fallback?: (props: { error: Error; reset: () => void }) => React.ReactNode;
  /**
   * "route" shows a centred card with error details.
   * "component" shows a compact inline placeholder.
   */
  level?: "route" | "component";
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  state: SectionErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<SectionErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { section } = this.props;
    console.error(
      `[SectionErrorBoundary:${section}] Uncaught render error:`,
      error,
      info.componentStack
    );
    this.submitCrashReport(error, info);
  }

  private async submitCrashReport(error: Error, info: ErrorInfo): Promise<void> {
    if (!this.shouldSendReport()) return;

    try {
      const consoleLogs = getCollectedLogs();
      const browserInfo = getBrowserInfo();

      let userId: string | null = null;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        userId = user?.id ?? null;
      } catch {
        // Auth unavailable — continue without user_id
      }

      const row = {
        user_id: userId,
        error_name: error.name,
        error_message: `[${this.props.section}] ${error.message}`.slice(0, 2000),
        error_stack: error.stack?.slice(0, 8000) ?? null,
        component_stack: info.componentStack?.slice(0, 8000) ?? null,
        page_url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        console_logs: consoleLogs,
        browser_info: browserInfo,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: insertError } = await (supabase.from as any)("crash_reports").insert(row);

      if (insertError && userId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase.from as any)("crash_reports").insert({ ...row, user_id: null });
      }

      this.markReportSent();
    } catch {
      // Crash-report submission itself failed — swallow silently
    }
  }

  private shouldSendReport(): boolean {
    try {
      const last = localStorage.getItem(CRASH_REPORT_COOLDOWN_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) return false;
    } catch {
      // localStorage unavailable
    }
    return true;
  }

  private markReportSent(): void {
    try {
      localStorage.setItem(CRASH_REPORT_COOLDOWN_KEY, String(Date.now()));
    } catch {
      // Ignore
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error } = this.state;
    const { fallback, section, level = "route" } = this.props;

    if (fallback && error) {
      return fallback({ error, reset: this.handleReset });
    }

    if (level === "component") {
      return <ComponentFallback section={section} onReset={this.handleReset} />;
    }

    return <RouteFallback section={section} error={error} onReset={this.handleReset} />;
  }
}

export default SectionErrorBoundary;

/* ------------------------------------------------------------------ */
/*  Convenience wrappers                                               */
/* ------------------------------------------------------------------ */

export function RouteErrorBoundary({
  children,
  section,
}: {
  children: React.ReactNode;
  section: string;
}) {
  return (
    <SectionErrorBoundary section={section} level="route">
      {children}
    </SectionErrorBoundary>
  );
}

export function ComponentErrorBoundary({
  children,
  section,
  fallback,
}: {
  children: React.ReactNode;
  section: string;
  fallback?: (props: { error: Error; reset: () => void }) => React.ReactNode;
}) {
  return (
    <SectionErrorBoundary section={section} level="component" fallback={fallback}>
      {children}
    </SectionErrorBoundary>
  );
}

/* ------------------------------------------------------------------ */
/*  Fallback UIs                                                       */
/* ------------------------------------------------------------------ */

function RouteFallback({
  section,
  error,
  onReset,
}: {
  section: string;
  error: Error | null;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-4xl">⚠️</div>
        <h2 className="text-xl font-semibold text-foreground">Chyba v sekci „{section}"</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Tato část aplikace narazila na neočekávanou chybu. Zbytek aplikace funguje normálně.
        </p>
        {error && (
          <pre className="text-left bg-muted border rounded-lg p-3 text-xs text-destructive overflow-auto max-h-32 whitespace-pre-wrap break-words">
            {error.name}: {error.message}
          </pre>
        )}
        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Zkusit znovu
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
          >
            Načíst celou stránku
          </button>
        </div>
      </div>
    </div>
  );
}

function ComponentFallback({ section, onReset }: { section: string; onReset: () => void }) {
  return (
    <div className="flex items-center justify-center p-4 rounded-lg border border-dashed border-destructive/30 bg-destructive/5 min-h-[120px]">
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Nepodařilo se načíst: <span className="font-medium">{section}</span>
        </p>
        <button
          onClick={onReset}
          className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Zkusit znovu
        </button>
      </div>
    </div>
  );
}
